import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { allMovies } from '@/mocks/movies';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movie = allMovies.find((m) => m.id === id);

  if (!movie) {
    return <NotFound />;
  }

  const relatedMovies = allMovies
    .filter((m) => m.categorySlug === movie.categorySlug && m.id !== movie.id)
    .slice(0, 6);

  const otherRecommendations = allMovies
    .filter((m) => m.id !== movie.id)
    .slice(0, 6);

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        {/* Breadcrumb */}
        <div className="px-4 md:px-6 py-4">
          <div className="max-w-[1400px] mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-400 flex-wrap" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${movie.categorySlug}`} className="hover:text-primary-500 transition-colors cursor-pointer whitespace-nowrap">{movie.category}</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-600 truncate max-w-[200px]">{movie.title}</span>
            </nav>
          </div>
        </div>

        {/* Movie Detail Hero */}
        <section className="px-4 md:px-6 py-6 md:py-10">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex flex-col md:flex-row gap-8">
              {/* Poster */}
              <div className="flex-shrink-0 mx-auto md:mx-0">
                <div className="w-48 md:w-64 rounded-lg overflow-hidden bg-background-100">
                  <div className="aspect-[2/3] relative">
                    <img
                      src={movie.coverImage}
                      alt={movie.title}
                      className="w-full h-full object-cover"
                    />
                    {movie.isVip && (
                      <div className="absolute top-3 right-3 px-2.5 py-1 bg-accent-500 text-background-950 text-xs font-bold rounded-md">
                        VIP
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>
                  {movie.title}
                </h1>
                <p className="text-foreground-400 text-sm mb-4">{movie.titleEn}</p>

                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="flex items-center gap-1 px-2.5 py-1 bg-accent-500/10 text-accent-500 text-sm font-semibold rounded-md">
                    <i className="ri-star-fill"></i> {movie.rating}
                  </span>
                  <span className="px-2.5 py-1 bg-background-100 text-foreground-500 text-xs rounded-md">{movie.year}</span>
                  <span className="px-2.5 py-1 bg-background-100 text-foreground-500 text-xs rounded-md">{movie.region}</span>
                  <span className="px-2.5 py-1 bg-background-100 text-foreground-500 text-xs rounded-md">{movie.duration}</span>
                  <span className="px-2.5 py-1 bg-background-100 text-foreground-500 text-xs rounded-md">{movie.episodeCount}</span>
                  <span className="px-2.5 py-1 bg-primary-500/10 text-primary-500 text-xs rounded-md">{movie.category}</span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6 text-sm">
                  <div>
                    <span className="text-foreground-400">导演：</span>
                    <span className="text-foreground-600">{movie.director}</span>
                  </div>
                  <div>
                    <span className="text-foreground-400">上映日期：</span>
                    <span className="text-foreground-600">{movie.releaseDate}</span>
                  </div>
                  <div>
                    <span className="text-foreground-400">更新时间：</span>
                    <span className="text-foreground-600">{movie.updateDate}</span>
                  </div>
                  <div>
                    <span className="text-foreground-400">类型：</span>
                    <span className="text-foreground-600">{movie.genre.join(' / ')}</span>
                  </div>
                </div>

                <div className="mb-6">
                  <p className="text-foreground-400 text-xs mb-2">主演</p>
                  <div className="flex flex-wrap gap-2">
                    {movie.cast.map((actor) => (
                      <span key={actor} className="px-3 py-1.5 bg-background-100 text-foreground-500 text-xs rounded-full">
                        {actor}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <a
                    href={`#watch-${movie.id}`}
                    className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 rounded-md text-sm font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-2"
                  >
                    <i className="ri-play-circle-line text-lg"></i>
                    立即观看
                  </a>
                  <button className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-600 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-2">
                    <i className="ri-heart-line text-lg"></i>
                    收藏
                  </button>
                  <button className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-600 rounded-md text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer flex items-center gap-2">
                    <i className="ri-share-forward-line text-lg"></i>
                    分享
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Description */}
        <section className="px-4 md:px-6 py-8 md:py-10 bg-background-100">
          <div className="max-w-[1400px] mx-auto">
            <div className="max-w-4xl">
              <h2 className="text-lg font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                剧情简介
              </h2>
              <article>
                <p className="text-foreground-500 text-sm leading-relaxed md:leading-loose">
                  {movie.longDescription}
                </p>
              </article>
            </div>
          </div>
        </section>

        {/* Related Movies */}
        {relatedMovies.length > 0 && (
          <section className="px-4 md:px-6 py-8 md:py-10">
            <div className="max-w-[1400px] mx-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                  同类推荐
                </h2>
                <Link
                  to={`/category/${movie.categorySlug}`}
                  className="text-sm text-foreground-400 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看更多 <i className="ri-arrow-right-s-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} variant="poster" />
                ))}
              </div>
            </div>
          </section>
        )}

        {/* More Recommendations */}
        <section className="px-4 md:px-6 py-8 md:py-10 bg-background-100">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-bold text-foreground-950" style={{ fontFamily: 'var(--font-heading)' }}>
                猜你喜欢
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
              {otherRecommendations.map((m) => (
                <MovieCard key={`rec-${m.id}`} movie={m} variant="poster" />
              ))}
            </div>
          </div>
        </section>
      </main>

      {/* Schema.org Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: 'https://7777cinema.com/' },
              { '@type': 'ListItem', position: 2, name: movie.category, item: `https://7777cinema.com/category/${movie.categorySlug}` },
              { '@type': 'ListItem', position: 3, name: movie.title, item: `https://7777cinema.com/article/${movie.id}` },
            ],
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Movie',
            name: movie.title,
            description: movie.description,
            image: movie.coverImage,
            dateCreated: movie.releaseDate,
            director: { '@type': 'Person', name: movie.director },
            actor: movie.cast.map((name) => ({ '@type': 'Person', name })),
            genre: movie.genre,
            countryOfOrigin: { '@type': 'Country', name: movie.region === '中国大陆' ? 'CN' : movie.region },
          }),
        }}
      />

      <Footer />
    </>
  );
}